import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IIntegration, IntegrationType } from '@principle-theorem/integrations';
import {
  isDisabled$,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { TNZIntegrationStorage } from '@principle-theorem/principle-core';
import {
  IBaseSMSIntegrationData,
  ITNZIntegrationData,
  SmsIntegrationOption,
} from '@principle-theorem/principle-core/interfaces';
import {
  CollectionReference,
  Region,
  snapshot,
} from '@principle-theorem/shared';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { StripeSMSPricingSelectorComponent } from '../stripe-sms-pricing-selector/stripe-sms-pricing-selector.component';
import { IsManagementDirective } from '@principle-theorem/ng-principle-shared';

type BasicFormData = Pick<ITNZIntegrationData, 'testModeEnabled'>;
type ManagementFormData = Pick<ITNZIntegrationData, 'enabled'>;

interface ITNZPricing {
  inboundCost: number;
  outboundCost: number;
}

@Component({
  selector: 'pr-manage-tnz-integration',
  templateUrl: './manage-tnz-integration.component.html',
  styleUrl: './manage-tnz-integration.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatSlideToggle,
    ReactiveFormsModule,
    MatInputModule,
    MatButton,
    StripeSMSPricingSelectorComponent,
    IsManagementDirective,
  ],
})
export class ManageTnzIntegrationComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _integrationsCol$ = new ReplaySubject<
    CollectionReference<IIntegration<ITNZIntegrationData>>
  >(1);
  private _tnzStorage = new TNZIntegrationStorage();
  private _savedProvider$ = new ReplaySubject<SmsIntegrationOption | undefined>(
    1
  );
  orgSlug$ = new ReplaySubject<string>(1);
  enabled$: Observable<boolean>;
  data$: Observable<ITNZIntegrationData>;
  form = new TypedFormGroup<BasicFormData>({
    testModeEnabled: new TypedFormControl<boolean>(false),
  });
  managementForm = new TypedFormGroup<ManagementFormData>({
    enabled: new TypedFormControl<boolean>(false),
  });
  isDisabled$ = isDisabled$(this.managementForm);
  pricing$: Observable<ITNZPricing | undefined>;
  pricingUpdate = new BehaviorSubject<IBaseSMSIntegrationData | undefined>(
    undefined
  );

  region = input.required<Region>();

  @Output() providerChanged = new EventEmitter<SmsIntegrationOption>();

  @Input()
  set integrationsCol(
    integrationsCol: CollectionReference<IIntegration<ITNZIntegrationData>>
  ) {
    if (!integrationsCol) {
      return;
    }
    this._integrationsCol$.next(integrationsCol);
  }

  @Input()
  set orgSlug(slug: string) {
    if (slug) {
      this.orgSlug$.next(slug);
    }
  }

  @Input()
  set savedProvider(provider: SmsIntegrationOption | undefined) {
    this._savedProvider$.next(provider);
  }

  constructor(private _snackBar: MatSnackBar) {
    this.enabled$ = this._integrationsCol$.pipe(
      switchMap((integrationsCol) => this._tnzStorage.get$(integrationsCol)),
      map((integration) => !!integration?.data.enabled)
    );

    this.data$ = this._integrationsCol$.pipe(
      switchMap((integrationsCol) => this._tnzStorage.get$(integrationsCol)),
      map((data) => data?.data ?? TNZIntegrationStorage.init({}))
    );

    this.data$.pipe(take(1), takeUntil(this._onDestroy$)).subscribe((data) => {
      this.form.patchValue(data, { emitEvent: false });
      this.managementForm.patchValue(data, { emitEvent: false });
    });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async save(): Promise<void> {
    const savedProvider = await snapshot(this._savedProvider$);
    if (savedProvider !== IntegrationType.TNZ) {
      this.providerChanged.emit(IntegrationType.TNZ);
    }
    const col = await snapshot(this._integrationsCol$);
    const formData = this.form.getRawValue();
    const managementFormData = this.managementForm.getRawValue();
    const pricing = await snapshot(this.pricingUpdate);
    await this._tnzStorage.upsert(col, {
      ...formData,
      ...managementFormData,
      ...(pricing || {}),
    });
    this._snackBar.open('Settings Updated');
  }
}
